import React, { useState, useEffect } from "react";
import {
    Grid,
    IconButton,
    Typography,
    Paper,
    Dialog,
    DialogContent,
    TextField,
    Box,
    Card,
    CardContent,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Container,
    InputAdornment,
    Button,
} from "@mui/material";
import {
    ArrowBackIos,
    ArrowForwardIos,
    CalendarToday,
    Search,
    Close,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import API_ENDPOINTS from "../../apis/endpoints";
import SearchInput from "../../components/SearchInput";
import useAuth from "../../context";
import DefaultImage from "../../assets/images/default_image.png";

const TopicalCalendar = () => {
    const [currentWeek, setCurrentWeek] = useState(new Date());
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [category, setCategory] = useState("all");
    const [holidays, setHolidays] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [isSearchActive, setIsSearchActive] = useState(false);

    const auth = useAuth();

    const storedDeviceId = auth.deviceId;
    const storedToken = auth.accessToken;

    useEffect(() => {
        fetchHolidays(currentWeek);
    }, [currentWeek]);

    const fetchHolidays = async (date) => {
        setLoading(true);
        try {
            const formattedDate = formatDate(date);
            const response = await fetch(
                `${API_ENDPOINTS.CALENDAR}?from=${formattedDate}`,
                {
                    method: "GET",
                    headers: {
                        "ngrok-skip-browser-warning": "true",
                        "x-cc-device-id": storedDeviceId,
                        Authorization: storedToken,
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const data = await response.json();
            setHolidays(data.data.weekly);
        } catch (error) {
            console.error("Error fetching holidays:", error);
        }
        setLoading(false);
    };

    const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const getWeekDates = (date) => {
        const week = [];
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay());

        for (let i = 0; i < 7; i++) {
            const day = new Date(startOfWeek);
            day.setDate(startOfWeek.getDate() + i);
            week.push(day);
        }
        return week;
    };

    const weekDates = getWeekDates(currentWeek);

    const navigateWeek = (direction) => {
        const newDate = new Date(currentWeek);
        newDate.setDate(newDate.getDate() + direction * 7);
        setCurrentWeek(newDate);
    };

    const handleCalendarSelect = (date) => {
        setCurrentWeek(date);
        setCalendarOpen(false);
    };

    const handleCategoryChange = (event) => {
        setCategory(event.target.value);
    };

    const handleDateClick = (date) => {
        setSelectedDate(date);
    };

    const handleSearch = async (searchQuery = searchTerm) => {
        // If search query is empty, reset to original data
        if (searchQuery.trim() === "") {
            setSearchResults([]);
            setIsSearchActive(false);
            fetchHolidays(currentWeek);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                `${API_ENDPOINTS.CALENDAR}?query=${searchQuery}`,
                {
                    method: "GET",
                    headers: {
                        "ngrok-skip-browser-warning": "true",
                        "x-cc-device-id": storedDeviceId,
                        Authorization: storedToken,
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }

            const data = await response.json();

            if (data?.success && data?.data) {
                setSearchResults(data?.data);
                setIsSearchActive(true);
            } else {
                setSearchResults([]);
                setIsSearchActive(false);
            }
        } catch (error) {
            console.error("Error searching holidays:", error);
            setSearchResults([]);
            setIsSearchActive(false);
        }
        setLoading(false);
    };

    const handleCloseSearch = () => {
        setSearchTerm("");
        setSearchResults([]);
        setIsSearchActive(false);
        fetchHolidays(currentWeek);
    };

    const handleKeyPress = (event) => {
        if (event.key === "Enter") {
            handleSearch();
        }
    };

    const getSelectedDateSpecialDays = () => {
        const formattedDate = formatDate(selectedDate);
        return holidays.filter((holiday) => holiday.date === formattedDate);
    };

    const filteredHolidays =
        searchResults.length > 0
            ? searchResults
            : holidays.filter(
                  (holiday) =>
                      category === "all" || holiday.category === category
              );

    const HolidayCard = ({ holiday }) => {
        // State to manage the image source
        const [imageSrc, setImageSrc] = useState(holiday.image);

        // Function to handle image loading error
        const handleImageError = () => {
            setImageSrc(DefaultImage); // Replace with your default image path
        };

        return (
            <Card sx={{ display: "flex", width: "100%" }}>
                <CardContent sx={{ flex: 1 }}>
                    <Typography variant="body2" color="primary">
                        {holiday.category}
                    </Typography>
                    <Typography variant="h6" component="div">
                        {holiday.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {holiday.date}
                    </Typography>
                </CardContent>
                {holiday.image && (
                    <Box
                        sx={{
                            width: 100,
                            height: 100,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 1,
                        }}
                    >
                        {" "}
                        <img
                            src={imageSrc}
                            alt={holiday.name}
                            onError={handleImageError} // Handle image loading error
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "10px",
                            }}
                        />
                    </Box>
                )}
            </Card>
        );
    };

    return (
        <Container sx={{ mt: 4, mb: 4 }}>
            <Paper
                elevation={3}
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                    backgroundColor: "background.paper",
                    display: "flex",
                    flexDirection: "column",
                    padding: 2,
                    overflow: "visible",
                    mb: 4,
                }}
            >
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2 }}
                >
                    <IconButton onClick={() => navigateWeek(-1)}>
                        <ArrowBackIos />
                    </IconButton>
                    <Typography variant="h6">
                        {`${weekDates[0].toLocaleDateString()} - ${weekDates[6].toLocaleDateString()}`}
                    </Typography>
                    <IconButton onClick={() => navigateWeek(1)}>
                        <ArrowForwardIos />
                    </IconButton>
                    <IconButton onClick={() => setCalendarOpen(true)}>
                        <CalendarToday />
                    </IconButton>
                </Grid>

                <Grid container spacing={2}>
                    {weekDates.map((date, index) => (
                        <Grid item xs key={index}>
                            <Paper
                                elevation={1}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    p: 1,
                                    cursor: "pointer",
                                    bgcolor:
                                        selectedDate &&
                                        date.toDateString() ===
                                            selectedDate.toDateString()
                                            ? "primary.light"
                                            : "inherit",
                                    "&:hover": {
                                        bgcolor: "action.hover",
                                    },
                                    height: "80px",
                                    minWidth: "80px",
                                }}
                                onClick={() => handleDateClick(date)}
                            >
                                <Typography variant="subtitle2">
                                    {date.toLocaleDateString("en-US", {
                                        weekday: "short",
                                    })}
                                </Typography>
                                <Typography variant="h6">
                                    {date.getDate()}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {getSelectedDateSpecialDays().length > 0 && (
                <Box
                    sx={{
                        my: 2,
                        p: 2,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                    }}
                >
                    <Grid container spacing={2}>
                        {getSelectedDateSpecialDays().map(
                            (specialDay, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <HolidayCard holiday={specialDay} />
                                </Grid>
                            )
                        )}
                    </Grid>
                </Box>
            )}

            <Grid container spacing={3} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                    <Box
                        sx={{
                            mb: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <SearchInput
                            onSearch={handleSearch}
                            placeHolder={"Events..."}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} md={4} sx={{ ml: "auto" }}>
                    <FormControl fullWidth>
                        <InputLabel id="category-select-label">
                            Category
                        </InputLabel>
                        <Select
                            labelId="category-select-label"
                            id="category-select"
                            value={category}
                            label="Category"
                            onChange={handleCategoryChange}
                        >
                            <MenuItem value="all">All Categories</MenuItem>
                            {[...new Set(holidays.map((h) => h.category))].map(
                                (cat) => (
                                    <MenuItem key={cat} value={cat}>
                                        {cat}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {filteredHolidays.map((holiday) => (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={holiday.date + holiday.name}
                        >
                            <HolidayCard holiday={holiday} />
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)}>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Select Week"
                            value={currentWeek}
                            onChange={handleCalendarSelect}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </LocalizationProvider>
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default TopicalCalendar;
